import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { forkJoin, fromEventPattern, Observable, Subject, timer } from 'rxjs';
import { debounceTime, filter, take, tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';

@Component({
    selector: 'cmps-comment',
    templateUrl: './comment.component.html',
    host: {
        class: 'cmps-comment',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CmpsCommentComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class CmpsCommentComponent
    implements OnInit, OnDestroy, ControlValueAccessor, TakeUntilDestroy
{
    @ViewChild(EditorComponent)
    private readonly _editor: EditorComponent;

    @Input() placeholder: string;

    @Input() loadEditorWhenVisible: boolean;

    @Input() showToolbar: boolean | null;

    @Input() fillParentContainer: boolean | null;

    /** @private */
    _show: boolean;

    /** @private */
    _showToolbar: boolean;

    /** @private */
    _isDisabled: boolean;

    /** @private */
    _content: string;

    // TODO types
    /** @private */
    _init = {
        menubar: false,
        toolbar:
            'formatselect bold italic link bullist numlist blockquote undo redo',
        statusbar: false,
        default_link_target: '_blank',
        target_list: false,
        placeholder: null,
        autoresize_bottom_margin: 0,
        browser_spellcheck: true,
        contextmenu: false,
        // content_css: 'default',
        content_style:
            'p { margin-block-start: 0.1rem; margin-block-end: 0.1rem; } ' +
            'body { background-color: transparent; line-height: 1; }',
        setup: () => {
            this._changeDetectorRef.detectChanges();
            // TODO revise for versions > 3.6.1. needed 'cause editor doesn't disable when setDisabledState is called before underlying editor is ready
            this._editor?.setDisabledState(this._isDisabled);
        },
        plugins: 'autoresize link lists',
        toolbar_location: 'bottom',
        height: 0,
    };

    private _onChange: (_: any) => void;
    private _onTouched: () => void;

    private _contentChangeSubj: Subject<string>;

    constructor(
        private readonly _el: ElementRef,
        private readonly _changeDetectorRef: ChangeDetectorRef,
    ) {
        this.loadEditorWhenVisible = true;
    }

    ngOnInit(): void {
        this._init.placeholder = this.placeholder;
        this._showToolbar = this.showToolbar;

        this._contentChangeSubj = new Subject();
        this._contentChangeSubj
            .pipe(
                debounceTime(500),
                tap((value) => {
                    this._onChange?.(value);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._bindCheckFillParentContainer();
    }

    ngOnDestroy(): void {
        this._contentChangeSubj?.complete();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
        this._editor?.setDisabledState(isDisabled);
    }

    writeValue(obj: string): void {
        this._content = obj;
        this._delayInitialEditorRender();
    }

    focus(): void {
        // setTimeout(() => {
        this._showToolbar = this.showToolbar ?? true; // TODO revise for versions > 3.6.1. with tinymce it seems like onFocus doesn't always emit when focus is called
        this._editor.editor.focus();
        // }, 300);
    }

    /** ensure toolbar dismiss when editor.focus is called elsewhere */
    blur(): void {
        // setTimeout(() => {
        this._showToolbar = this.showToolbar ?? false;
        // }, 300);
        this._onTouched?.();
    }

    /** @private */
    _onContentChange(value: string): void {
        this._contentChangeSubj.next(value);
    }

    /** attempt to slightly delay the underlying editor iframe,
     * so that the browser doesn't wait for it when doing/scheduling a significant painting
     * in the context of a full commitment/observation form load. maybe iframe lazy-load */
    private _delayInitialEditorRender(): void {
        if (this._show) {
            return;
        }
        const obses: Array<Observable<any>> = [timer(50)];
        if (this.loadEditorWhenVisible) {
            let io: IntersectionObserver;
            obses.push(
                fromEventPattern(
                    (handler) => {
                        io = new IntersectionObserver(handler, {
                            rootMargin: '100px',
                            threshold: 0,
                        });
                        io.observe(this._el.nativeElement);
                    },
                    () => {
                        io.disconnect();
                    },
                ).pipe(
                    filter((val) => val?.[0]?.[0]?.isIntersecting),
                    take(1),
                ),
            );
        }
        forkJoin(obses)
            .pipe(
                tap(() => {
                    this._checkFillParentContainer();
                    this._show = true;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bindCheckFillParentContainer(): void {
        let io: ResizeObserver;
        fromEventPattern(
            (handler) => {
                io = new ResizeObserver(handler);
                io.observe(this._el.nativeElement.parentElement);
            },
            () => {
                io.disconnect();
            },
        )
            .pipe(
                debounceTime(100),
                tap(() => {
                    this._checkFillParentContainer();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _checkFillParentContainer(): void {
        if (!this.fillParentContainer) {
            return;
        }

        const rect = (
            this._el.nativeElement.parentElement as HTMLElement
        ).getBoundingClientRect();
        const init = this._init as any;
        init.height = rect.height;
        init.min_height = rect.height;
        init.max_height = rect.height;

        const el: HTMLElement = this._editor?.editor?.getContainer();
        if (el != null) {
            el.style.height = `${rect.height}px`;
        }
    }
}
